import type { RtdbClient } from '@akpl/firebase/client';
import { type Presence, rtdbPaths } from '@akpl/shared';

/** Refresh interval for `lastSeen` while the app stays online. */
export const PRESENCE_HEARTBEAT_MS = 60_000;

export interface TrackPresenceOptions {
  heartbeatMs?: number;
  now?: () => number;
  onError?: (error: Error) => void;
}

const offline = (now: number): Presence => ({ online: false, lastSeen: now });
const online = (now: number): Presence => ({ online: true, lastSeen: now });

/**
 * Keeps `members/{uid}/presence` current (docs/02 §2 — the only member field a client may
 * write, and only for its own uid). An `onDisconnect` registration flips it to `offline` when
 * the socket drops; a heartbeat refreshes `lastSeen` while the app is up; leaving writes
 * `offline` immediately.
 *
 * The caller is responsible for only calling this with a uid that is a member of `sid`: a TV
 * token is not a member (its uid is not under `members/`), so the rules would reject it —
 * `usePresence` checks the store before mounting this.
 */
export function trackPresence(
  client: RtdbClient,
  sid: string,
  uid: string,
  options: TrackPresenceOptions = {},
): () => void {
  const { heartbeatMs = PRESENCE_HEARTBEAT_MS, now = Date.now, onError } = options;
  const path = rtdbPaths.presence(sid, uid);
  const report = (error: unknown) =>
    onError?.(error instanceof Error ? error : new Error(String(error)));

  let stopped = false;
  let cancelOnDisconnect: (() => Promise<void>) | null = null;

  void (async () => {
    try {
      // Register the "gone" value first, so a drop between the two writes still cleans up.
      const cancel = await client.onDisconnectSet(path, offline(now()));
      if (stopped) {
        await cancel();
        return;
      }
      cancelOnDisconnect = cancel;
      await client.write(path, online(now()));
    } catch (error) {
      report(error);
    }
  })();

  const heartbeat = setInterval(() => {
    client.write(path, online(now())).catch(report);
  }, heartbeatMs);

  return () => {
    if (stopped) return;
    stopped = true;
    clearInterval(heartbeat);
    void (async () => {
      try {
        await cancelOnDisconnect?.();
        await client.write(path, offline(now()));
      } catch (error) {
        report(error);
      }
    })();
  };
}
